import type { EncodedDataWithCaptions, EncodedVideoData } from './demuxerTypes';
import { demuxTsSegmentWithCaptions } from './mpegTsDemuxer';
import isEqual from 'lodash/isEqual';

interface TsFrame {
  /**
   * Presentation timestamp. Divide by 90000 (90Hz) to get seconds.
   */
  pts: number;

  /**
   * Decoding timestamp. Divide by 90000 (90Hz) to get seconds.
   */
  dts: number;
}


export interface TsSegmentInfo {
  // These timestamp refer to the "primary" stream. For video this will be the video stream.
  // For audio only this will be the first audio stream.
  firstPts: number;
  firstDts: number;
  lastPts: number;
  hasVideo: boolean;
  hasAudio: boolean;
  encodedDataWithCaptions: EncodedDataWithCaptions;
}

function firstAndLast<T>(list: T[]): [T, T] {
  return [list[0]!, list[list.length - 1]!];
}

export function inspect(bytes: Uint8Array, firstSegment: boolean): TsSegmentInfo | null {
  const encodedDataWithCaptions = demuxTsSegmentWithCaptions(bytes, firstSegment);
  let video : TsFrame[] | undefined = undefined;
  let audio : TsFrame[] | undefined = undefined;

  if (encodedDataWithCaptions.encodedVideoData !== undefined && encodedDataWithCaptions.encodedVideoData.frames.length > 0) {
    const [firstVideo, lastVideo] = firstAndLast(encodedDataWithCaptions.encodedVideoData.frames);
    if (!firstVideo.keyframe) {
      throw new Error('First video frame is not a keyframe');
    }
    video = [firstVideo, lastVideo]
      .map((encodedVideoFrame) => {
        const tsFrame : TsFrame = {
          pts: encodedVideoFrame.pts,
          dts: encodedVideoFrame.dts,
        };
        return tsFrame;
      });
  }
  for (let audioStreamIndex = 0; audioStreamIndex < encodedDataWithCaptions.encodedAudioData.length; audioStreamIndex++) {
    const encodedAudioData = encodedDataWithCaptions.encodedAudioData[audioStreamIndex];
    if (encodedAudioData!.frames.length == 0) {
      throw new Error(`No audio packets for audio stream #${audioStreamIndex}`);
    }
    const streamTsAudioFrame : TsFrame[] =
      firstAndLast(encodedAudioData!.frames)
        .map((encodedAudioFrame) => {
          const tsFrame : TsFrame = {
            pts: encodedAudioFrame.pts,
            dts: encodedAudioFrame.dts,
          };
          return tsFrame;
        });

    if (audio === undefined) {
      audio = streamTsAudioFrame;
    } else if (!isEqual(audio, streamTsAudioFrame)) {
      throw new Error(`Inconsistent audio packet availability between audio streams. First and last timestamps of audio stream 0: ${JSON.stringify(audio)}. First and last timestamps of audio stream ${audioStreamIndex}: ${JSON.stringify(streamTsAudioFrame)}.`);
    }
  }

  let primaryStreamFrames : TsFrame[] | TsFrame[];
  if (video && (video.length > 0)) {
    primaryStreamFrames = video;
  } else if (audio && (audio.length > 0)) {
    primaryStreamFrames = audio;
  } else {
    throw new Error('Media segment contains no media packets.');
  }

  const firstFrame = primaryStreamFrames[0]!;
  const lastFrame = primaryStreamFrames[primaryStreamFrames.length - 1]!


  return {
    firstPts: firstFrame.pts,
    firstDts: firstFrame.dts,
    lastPts: lastFrame.pts,
    hasVideo: video !== undefined,
    hasAudio: audio !== undefined,
    encodedDataWithCaptions,
  };
}


export function getVideoDurationInMs(videoData: EncodedVideoData): number {
  return (videoData.frames.length * videoData.frameDuration) / 90;
}
